/**
 * Traceability (CC-LINKS stage 2). The cross-workshop "why did we decide
 * this?" surface: create a link between any two records (in the same or
 * different workshops), browse the link graph, and jump to either endpoint.
 * Links live in the global `useLinks` store; reverse edges are computed on
 * read (`linksTouching`), never stored. Clicking an endpoint routes to its
 * workshop and opens the right modal (rAF-deferred for the cross-workshop
 * case, per Risk #7).
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Workflow, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { RemoveButton } from "@/features/workshop/modals/log-rows";
import { WORKSHOPS } from "@/content/workshops";
import { LINK_RELATIONS, LINK_RELATION_DEFAULT, type LinkRelation } from "@/lib/taxonomy";
import {
  LINK_ENDPOINT_LABEL,
  LINK_RELATION_LABEL,
  listLinkableEntities,
  resolveEndpoint,
} from "@/lib/links";
import type { SearchCategory } from "@/lib/search";
import { hydrateAllRegisters } from "@/features/missionControl/useProgramRollup";
import { useLinks, type LinkEndpoint } from "@/stores/useLinks";
import { useJumpTarget } from "@/features/search/useJumpTarget";
import { useWorkshopModals, type OpenModal } from "@/features/workshop/modals/useWorkshopModals";

type TraceabilityModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

function modalForCategory(category: SearchCategory): Exclude<OpenModal, null> | null {
  if (category === "capture") return "captures";
  if (category === "transcript") return "transcript";
  if (category === "upload") return null;
  return "decision-log";
}

/** Encode/decode an endpoint as a `<select>` value. */
const encodeEndpoint = (e: LinkEndpoint): string => `${e.kind}::${e.workshopId}::${e.id}`;

export function TraceabilityModal({ open, onOpenChange, workshopId }: TraceabilityModalProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const openModal = useWorkshopModals((s) => s.open);
  const auth = useAuth();
  const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";

  const links = useLinks((s) => s.links);
  const addLink = useLinks((s) => s.add);
  const removeLink = useLinks((s) => s.remove);

  const [fromKey, setFromKey] = useState("");
  const [toKey, setToKey] = useState("");
  const [relation, setRelation] = useState<LinkRelation>(LINK_RELATION_DEFAULT);

  useEffect(() => {
    if (!open) return;
    void useLinks.getState().hydrate();
    void hydrateAllRegisters();
  }, [open]);

  // Recompute the picker list each open (records may have changed). Reads
  // live snapshots; `links` in the dep array keeps the labels fresh after a
  // create/remove without a heavier subscription.
  const entities = useMemo(() => (open ? listLinkableEntities() : []), [open, links]);
  const byKey = useMemo(() => {
    const m = new Map<string, (typeof entities)[number]>();
    for (const e of entities) m.set(encodeEndpoint(e.endpoint), e);
    return m;
  }, [entities]);

  function submit(): void {
    const from = byKey.get(fromKey)?.endpoint;
    const to = byKey.get(toKey)?.endpoint;
    if (!from || !to) return;
    addLink({ id: crypto.randomUUID(), from, to, relation, createdAt: Date.now(), addedBy });
    setFromKey("");
    setToKey("");
    setRelation(LINK_RELATION_DEFAULT);
  }

  function goTo(endpoint: LinkEndpoint): void {
    const hit = resolveEndpoint(endpoint);
    if (!hit) return;
    useJumpTarget.getState().set({
      workshopId: endpoint.workshopId,
      category: hit.category,
      id: hit.id,
    });
    if (endpoint.workshopId !== workshopId) {
      navigate(`/workshop/${endpoint.workshopId}`, { state: { from: pathname + search } });
    }
    const target = modalForCategory(hit.category);
    if (target) {
      requestAnimationFrame(() => openModal(target));
    } else {
      onOpenChange(false);
    }
  }

  const sameEndpointSelected = fromKey !== "" && fromKey === toKey;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[820px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Traceability</DialogTitle>
        <DialogDescription className="sr-only">
          Link records across workshops and trace why a decision was made.
        </DialogDescription>
        <div className="modal-shell ig-trace" style={{ width: "100%", maxHeight: "85vh" }}>
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Workflow aria-hidden style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }} />
              <span className="modal-title">TRACEABILITY</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {links.length} link{links.length === 1 ? "" : "s"}
              </span>
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="modal-close-btn" aria-label="Close">
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Link creator */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <EntitySelect
                ariaLabel="Link from"
                value={fromKey}
                onChange={setFromKey}
                entities={entities}
                placeholder="From record…"
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <select
                  aria-label="Relation"
                  value={relation}
                  onChange={(e) => setRelation(e.target.value as LinkRelation)}
                  className={FIELD_CLASS}
                  style={{ width: 160, flex: "none" }}
                >
                  {LINK_RELATIONS.map((r) => (
                    <option key={r} value={r}>
                      {LINK_RELATION_LABEL[r]}
                    </option>
                  ))}
                </select>
                <EntitySelect
                  ariaLabel="Link to"
                  value={toKey}
                  onChange={setToKey}
                  entities={entities}
                  placeholder="To record…"
                  className="min-w-[200px] flex-1"
                />
                <button
                  type="button"
                  onClick={submit}
                  disabled={!fromKey || !toKey || sameEndpointSelected}
                  className="modal-close-btn inline-flex items-center gap-1.5"
                  style={{
                    flex: "none",
                    opacity: !fromKey || !toKey || sameEndpointSelected ? 0.5 : 1,
                    cursor: !fromKey || !toKey || sameEndpointSelected ? "default" : "pointer",
                  }}
                >
                  <Plus aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} /> LINK
                </button>
              </div>
              {sameEndpointSelected && (
                <p style={{ fontSize: "var(--fs-micro)", color: "var(--accent-melon)" }}>
                  Pick two different records to link.
                </p>
              )}
              {entities.length === 0 && (
                <p style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                  No records to link yet — capture some decisions, findings, or actions first.
                </p>
              )}
            </div>

            {/* Link list */}
            <ul className="flex flex-col gap-1.5">
              {links.length === 0 && (
                <li style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                  No links yet. Linking records answers "why did we decide this?" across workshops.
                </li>
              )}
              {links.map((link) => {
                const from = resolveEndpoint(link.from);
                const to = resolveEndpoint(link.to);
                return (
                  <li
                    key={link.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "rgba(var(--accent-true-blue-rgb), 0.04)",
                      border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <EndpointChip endpoint={link.from} label={from?.title} onClick={() => goTo(link.from)} />
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--text-muted)", fontSize: "var(--fs-micro)" }}>
                        <ArrowRight aria-hidden style={{ width: 12, height: 12 }} />
                        {LINK_RELATION_LABEL[link.relation]}
                        <ArrowRight aria-hidden style={{ width: 12, height: 12 }} />
                      </span>
                      <EndpointChip endpoint={link.to} label={to?.title} onClick={() => goTo(link.to)} />
                    </div>
                    <RemoveButton label="Remove link" onClick={() => removeLink(link.id)} />
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Traceability</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/** A clickable endpoint chip — kind + workshop + (resolved) title. A
 *  dangling endpoint (record deleted) shows muted "(removed)". */
function EndpointChip({
  endpoint,
  label,
  onClick,
}: {
  endpoint: LinkEndpoint;
  label?: string;
  onClick: () => void;
}) {
  const dangling = label == null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={dangling}
      title={`${LINK_ENDPOINT_LABEL[endpoint.kind]} · ${endpoint.workshopId}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        maxWidth: 280,
        padding: "2px 8px",
        borderRadius: 6,
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.22)",
        background: "rgba(var(--accent-true-blue-rgb), 0.06)",
        color: dangling ? "var(--text-muted)" : "var(--text-primary)",
        fontSize: "var(--fs-label)",
        cursor: dangling ? "default" : "pointer",
      }}
    >
      <span style={{ fontSize: "var(--fs-micro)", color: "var(--accent-sky)", letterSpacing: 0.4 }}>
        {endpoint.workshopId}
      </span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label ?? "(removed)"}
      </span>
    </button>
  );
}

/** A `<select>` over every linkable record, labelled "WS · Kind · Title". */
function EntitySelect({
  ariaLabel,
  value,
  onChange,
  entities,
  placeholder,
  className,
}: {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  entities: ReturnType<typeof listLinkableEntities>;
  placeholder: string;
  className?: string;
}) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(FIELD_CLASS, className)}
      style={className ? undefined : { width: "100%" }}
    >
      <option value="">{placeholder}</option>
      {entities.map(({ endpoint, hit }) => {
        const key = `${endpoint.kind}::${endpoint.workshopId}::${endpoint.id}`;
        const ws = WORKSHOPS.find((w) => w.id === endpoint.workshopId);
        return (
          <option key={key} value={key}>
            {(ws?.id ?? endpoint.workshopId)} · {LINK_ENDPOINT_LABEL[endpoint.kind]} · {hit.title}
          </option>
        );
      })}
    </select>
  );
}
